import React, { useEffect, useState } from 'react'
import Grid2 from '@mui/material/Grid2'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import ProgramSummaryContainer from 'features/Programs/Editing/Components/ProgramSummaryContainer'
import { Locations } from 'shared/Router/Locations'
import { useNavigate } from 'react-router-dom'
import { launchProgramsActions } from '../Launch/launchProgramActions'
import { programEditingActions } from './ProgramEditingActions'
import wordings from '../Shared/wordings.json'
import {
  getEstimatedMonthlySpend,
  validateLaunchPrograms,
  hasMaxAllocationLimitExceeded,
} from '../Shared/helperFunctions'
import AlertSuccessModal from 'core-system/Modal/AlertSuccessModal'
import { grhActions } from 'redux/grh/grhSlice'
import { micromobilityActions } from 'redux/micromobility/micromobilitySlice'
import { flexActions } from 'redux/flex/flexSlice'
import ProgramEditingView from './ProgramEditingView'
import ProgramSummaryView from '../Summary/ProgramSummaryView'
import { useLocation } from 'react-router-dom'
import { LaunchButtons } from './Components/LaunchButtons'
import WorksitesDateSelection from '../Launch/Components/WorksitesDateSelection'
import TextField from 'core-system/TextField/TextField'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import styled from 'styled-components'

const PageItem = styled.div`
  margin-bottom: 2.5rem;
`

const TitleContainer = styled(Text)`
  margin-bottom: 1rem;
  color: ${palette.text.primary};
`

const DescriptionTextArea = styled.textarea`
  width: 100%;
  min-height: 3rem;
  padding: 0.625rem 1rem;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.5rem;
  font: inherit;
  color: ${palette.text.primary};
  resize: vertical;

  &::placeholder {
    color: ${palette.text.placeholder};
  }

  &:focus {
    outline: none;
    border-color: ${palette.text.secondary};
  }
`

const ProgramsEditingSummaryView = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const isFromUpdate = location.state?.fromUpdate
  const programIds = location.state?.programIds
  const launchId = location.state?.launchId

  const initialSelectedSegments = location.state?.initialSelectedSegments || []

  const {
    selectedPrograms,
    selectedDate,
    isContinued,
    selectedSegments,
    programName,
    description,
    currency,
  } = useSelector((state: AppState) => state.launchPrograms)
  const { grh, qtf, micromobility, flex, transitPass, coupon, reportedUsage } =
    useSelector((state: AppState) => state.programEditing)
  const { irsTaxLimit } = useSelector(
    (state: AppState) => state.application.properties
  )
  const availableGrhMerchant = useSelector(
    (state: AppState) => state.grh.availableMerchants
  )
  const availableMicromobilityMerchant = useSelector(
    (state: AppState) => state.micromobility.availableMerchants
  )
  const availableFlexMerchant = useSelector(
    (state: AppState) => state.flex.availableMerchants
  )
  const { allSegments } = useSelector((state: AppState) => state.employer)
  const [alertModalState, setAlertModalState] = useState({
    open: false,
    modelType: 'alert' as 'alert' | 'success',
    headerText: '',
    message: '',
    buttonText: '',
    onButtonClick: () => {},
  })

  useEffect(() => {
    if (selectedPrograms.length === 0) {
      navigate(Locations.Programs.Overview)
    }
  }, [])

  const saveAsDraft = async () => {
    try {
      await dispatch(
        launchProgramsActions.saveDraftProgramState({
          selectedDate,
          selectedPrograms,
          selectedSegments,
          programName,
          description,
          currency,
          grh,
          qtf,
          micromobility,
          flex,
          transitPass,
          coupon,
        })
      )
      setAlertModalState({
        open: true,
        modelType: 'success',
        headerText: 'Draft saved',
        message: '',
        buttonText: 'OK',
        onButtonClick: () =>
          setAlertModalState({ ...alertModalState, open: false }),
      })
    } catch (error) {
      setAlertModalState({
        open: true,
        modelType: 'alert',
        headerText: 'Error saving draft',
        message: 'Please try again',
        buttonText: 'OK',
        onButtonClick: () =>
          setAlertModalState({ ...alertModalState, open: false }),
      })
    }
  }

  const handleProgramNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(launchProgramsActions.changeProgramName(e.target.value))
  }
  const handleProgramDescriptionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(launchProgramsActions.changeDescription(e.target.value))
  }

  const editPrograms = () => {
    // Validate program name
    if (!programName.trim()) {
      setAlertModalState({
        open: true,
        modelType: 'alert',
        headerText: 'Program name required',
        message: 'Please enter a program name before launching',
        buttonText: 'OK',
        onButtonClick: () =>
          setAlertModalState({ ...alertModalState, open: false }),
      })
      return
    }

    const validationResponse = validateLaunchPrograms(
      grh,
      qtf,
      micromobility,
      flex,
      transitPass,
      selectedDate,
      selectedPrograms,
      irsTaxLimit,
      selectedSegments,
      coupon,
      reportedUsage
    )
    const { isValid, message, heading } = validationResponse
    if (!isValid) {
      setAlertModalState({
        open: true,
        modelType: 'alert',
        headerText: heading,
        message: message,
        buttonText: 'OK',
        onButtonClick: () =>
          setAlertModalState({ ...alertModalState, open: false }),
      })
      return
    }
    // Discarding any draft state if the program is continued
    if (isContinued) {
      dispatch(launchProgramsActions.deleteDraftProgramState())
    }
    // If we are updating the programs, passing the program ids
    if (isFromUpdate) {
      navigate(Locations.Programs.Success, {
        state: {
          programIds,
          fromUpdate: true,
          initialSelectedSegments,
          launchId,
        },
      })
      return
    }
    // Launching the programs
    navigate(Locations.Programs.Success)
  }

  if (selectedPrograms.length === 0) {
    return null
  }

  const hasMaxLimitExceeded = hasMaxAllocationLimitExceeded(
    qtf,
    irsTaxLimit,
    selectedPrograms
  )
  const isLaunchDisabled = !programName.trim() || hasMaxLimitExceeded

  return (
    <>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, md: 8 }}>
          <PageItem>
            <TitleContainer variant='h5'>
              Program Name{' '}
              <span style={{ color: palette.error.red500 }}>*</span>
            </TitleContainer>
            <TextField
              value={programName}
              onChange={handleProgramNameChange}
              placeholder='Enter program name'
              required
            />
          </PageItem>
          <PageItem>
            <TitleContainer variant='h5'>Description</TitleContainer>
            <DescriptionTextArea
              value={description}
              onChange={handleProgramDescriptionChange}
              placeholder='Enter optional description'
            />
          </PageItem>
          {isFromUpdate && (
            <WorksitesDateSelection
              allSegments={allSegments}
              selectedSegments={selectedSegments}
              selectedDate={selectedDate}
              isFromUpdate={isFromUpdate}
            />
          )}
          <ProgramEditingView
            isContinued={isContinued}
            grh={grh}
            qtf={qtf}
            micromobility={micromobility}
            flex={flex}
            transitPass={transitPass}
            coupon={coupon}
            allSegments={allSegments}
            irsTaxLimit={irsTaxLimit}
            selectedPrograms={selectedPrograms}
            selectedDate={selectedDate}
            availableGrhMerchant={availableGrhMerchant}
            availableMicromobilityMerchant={availableMicromobilityMerchant}
            availableFlexMerchant={availableFlexMerchant}
            changeQtfBenefits={(type, value) =>
              dispatch(programEditingActions.changeQtfBenefits({ type, value }))
            }
            changeQtfEmployerContributions={(type, value) =>
              dispatch(
                programEditingActions.changeQtfEmployerContributions({
                  type,
                  value,
                })
              )
            }
            changeQtfFinalizeAllocation={(day) =>
              dispatch(programEditingActions.changeQtfFinalizeAllocation(day))
            }
            changeQtfPayrollSchedule={(payrollSchedule) =>
              dispatch(
                programEditingActions.changeQtfPayrollSchedule(payrollSchedule)
              )
            }
            changeQtfMaxAllocationLimit={(type, value) =>
              dispatch(
                programEditingActions.changeQtfMaxAllocationLimit({
                  type,
                  value,
                })
              )
            }
            getGrhRecommendations={() =>
              dispatch(grhActions.getGrhRecommendations({ segmentId: '' }))
            }
            changeGrhActiveMerchantIds={(type, value) =>
              dispatch(
                programEditingActions.changeGrhActiveMerchantIds({
                  type,
                  value,
                })
              )
            }
            changeGrhTransactionUses={(type, value) =>
              dispatch(
                programEditingActions.changeGrhTransactionUses({ type, value })
              )
            }
            changeGrhActiveOptions={(type, value) =>
              dispatch(
                programEditingActions.changeGrhActiveOptions({ type, value })
              )
            }
            getMicromobilityRecommendations={(id) =>
              dispatch(
                micromobilityActions.getMicromobilityRecommendations({
                  segmentId: id,
                })
              )
            }
            changeMicromobilityActiveMerchantIds={(type, value) =>
              dispatch(
                programEditingActions.changeMicromobilityActiveMerchantIds({
                  type,
                  value,
                })
              )
            }
            changeMicromobilityActiveOptions={(type, value) =>
              dispatch(
                programEditingActions.changeMicromobilityActiveOptions({
                  type,
                  value,
                })
              )
            }
            changeMicromobilityBudget={(budget) =>
              dispatch(programEditingActions.changeMicromobilityBudget(budget))
            }
            changeMicromobilityProrated={(value) =>
              dispatch(programEditingActions.changeMicromobilityProrated(value))
            }
            getFlexRecommendations={(id) =>
              dispatch(flexActions.getFlexRecommendations({ segmentId: id }))
            }
            changeFlexActiveMerchantIds={(type, value) =>
              dispatch(
                programEditingActions.changeFlexActiveMerchantIds({
                  type,
                  value,
                })
              )
            }
            changeFlexActiveOptions={(type, value) =>
              dispatch(
                programEditingActions.changeFlexActiveOptions({ type, value })
              )
            }
            changeFlexBudget={(budget) =>
              dispatch(programEditingActions.changeFlexBudget(budget))
            }
            changeFlexBudgetCycle={(cycle) =>
              dispatch(programEditingActions.changeFlexBudgetCycle(cycle))
            }
            changeFlexOneTimePurchase={(value) =>
              dispatch(programEditingActions.changeFlexOneTimePurchase(value))
            }
            changeFlexRolloverBudget={(value) =>
              dispatch(programEditingActions.changeFlexRolloverBudget(value))
            }
            changeFlexProrated={(value) =>
              dispatch(programEditingActions.changeFlexProrated(value))
            }
            changeTransitPassPurchaseType={(value) =>
              dispatch(
                programEditingActions.changeTransitPassPurchaseType(value)
              )
            }
            changeTransitPassWalletTopUpEnabled={(value) =>
              dispatch(
                programEditingActions.changeTransitPassWalletTopUpEnabled(
                  value
                )
              )
            }
            changeTransitPassBudget={(budget) =>
              dispatch(programEditingActions.changeTransitPassBudget(budget))
            }
            changeTransitPassDeliveryMechanism={(value) =>
              dispatch(
                programEditingActions.changeTransitPassDeliveryMechanism(value)
              )
            }
            changeTransitPassAgencyIds={(value) =>
              dispatch(programEditingActions.changeTransitPassAgencyIds(value))
            }
            changeTransitPassAgenciesEnabled={(value) =>
              dispatch(
                programEditingActions.changeTransitPassAgenciesEnabled(value)
              )
            }
            getTransitAgencies={() =>
              dispatch(programEditingActions.getTransitAgencies())
            }
            changeCouponMerchantIds={(value) =>
              dispatch(programEditingActions.changeCouponMerchantIds(value))
            }
            changeCouponMerchantsEnabled={(value) =>
              dispatch(programEditingActions.changeCouponMerchantsEnabled(value))
            }
            getCouponMerchants={() =>
              dispatch(programEditingActions.getCouponMerchants())
            }
            changeCouponCode={(value) =>
              dispatch(programEditingActions.changeCouponCode(value))
            }
            canEditAllocationsDate={isFromUpdate ? false : true}
            getGovernmentGrhPrograms={() =>
              dispatch(programEditingActions.getGovernmentGrhPrograms())
            }
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <ProgramSummaryContainer>
            <ProgramSummaryView
              selectedPrograms={selectedPrograms}
              monthlySpendDescription={wordings.monthlySpendDescription}
              grh={grh}
              qtf={qtf}
              micromobility={micromobility}
              flex={flex}
              transitPass={transitPass}
              coupon={coupon}
              selectedDate={selectedDate}
              title={wordings.summaryTitle}
              estimatedMonthlySpend={getEstimatedMonthlySpend(
                selectedPrograms,
                qtf,
                grh,
                micromobility,
                flex,
                transitPass
              )}
              selectedSegments={selectedSegments}
              displayType='drawer'
              allSegments={allSegments}
              irsTaxLimit={irsTaxLimit}
              currency={currency}
            />
          </ProgramSummaryContainer>
        </Grid2>
      </Grid2>
      <LaunchButtons
        isFromUpdate={isFromUpdate}
        editPrograms={editPrograms}
        saveAsDraft={saveAsDraft}
        isLaunchDisabled={isLaunchDisabled}
      />
      <AlertSuccessModal
        open={alertModalState.open}
        onClose={() => setAlertModalState({ ...alertModalState, open: false })}
        type={alertModalState.modelType}
        headerText={alertModalState.headerText}
        message={alertModalState.message}
        buttonText={alertModalState.buttonText}
        onButtonClick={() => {
          alertModalState.onButtonClick()
          setAlertModalState({ ...alertModalState, open: false })
        }}
      />
    </>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  ProgramsEditingSummaryView.displayName = 'ProgramsEditingSummaryView'
}

export default React.memo(ProgramsEditingSummaryView)
